import { useGetSuperAdminFranchisesFranchiseIdTranslationUsage } from '@agos/api-client/ai'
import {
  DEFAULT_USAGE_RANGE,
  T,
  USAGE_RANGES,
  emptyRangeNote,
  formatCost,
  formatCount,
  hasUsage,
  translationUsageOf,
  tt,
  usageRangeLabel,
  type UsageRange,
} from '@agos/shared'
import { ButtonGroup, StatCard } from '@agos/ui'
import { useMemo, useState } from 'react'

/**
 * Why a franchise's translations are failing - `SAAS.md` §3.89, `BACKEND.md` §10A.198.
 *
 * **The one thing the platform cannot answer from `api_usage_log`.** That log now carries *that* a
 * translation failed and its status code (§10A.198), which is enough to see a problem and not enough
 * to fix one. The message and the string that failed live in the franchise's own
 * `translation_usage` - 11 117 failure rows on the owner's data, every one with a message - and this
 * panel is the Tower's view of them.
 *
 * **Legacy had this and reached it by impersonation**: `FranchiseIntegrations.tsx` called the
 * *tenant* route with an `X-Franchise-Id` header that its own middleware documents as beating
 * everything. §10A.198 opens a `@RequireSuperAdmin` route instead, so nothing about the manager's
 * guard had to be widened.
 *
 * Two things are deliberately unlike legacy, both of them legacy bugs:
 *
 * - legacy's heading counts `recentErrors.length` - up to 20 - and then renders `.slice(0, 5)`, so
 *   it can say *(20)* above five rows. The count and the list agree here.
 * - legacy's message is `truncate` with no title and no expansion, so a 500-character Anthropic
 *   message is one clipped line. These wrap.
 *
 * And it renders the **source excerpt**, which legacy selects, types, ships and never draws. It is
 * the difference between *a translation failed* and *this string failed*: the restaurant's own menu
 * text, capped at 120 characters by the writer, and the manager's own panel already shows it.
 */
export function TranslationFailuresPanel({ franchiseId }: { franchiseId: number }) {
  const [range, setRange] = useState<UsageRange>(DEFAULT_USAGE_RANGE)

  // `range=all`, because a support question about a rejected API key is *has this ever worked* and
  // the restored data ends in July - a thirty-day default would answer *nothing logged* for a
  // franchise with 2 754 rejections.
  //
  // **And it stays the visibility question once a range can be picked - §3.98.** `hasUsage` hides
  // the whole section, switcher included, so gating on the *selected* range would let *Deň* on a
  // franchise last active in July make the panel disappear with no way back but a reload.
  const everQuery = useGetSuperAdminFranchisesFranchiseIdTranslationUsage(
    franchiseId,
    { range: DEFAULT_USAGE_RANGE },
    { query: { enabled: franchiseId > 0 } },
  )
  // Same key as the one above while the default is selected, so React Query issues one request and
  // the opened-fresh panel makes exactly the call it made before this entry.
  const query = useGetSuperAdminFranchisesFranchiseIdTranslationUsage(
    franchiseId,
    { range },
    { query: { enabled: franchiseId > 0 } },
  )
  const ever = useMemo(() => translationUsageOf(everQuery.data), [everQuery.data])
  const usage = useMemo(() => translationUsageOf(query.data), [query.data])

  /* FB-808, §3.149 - **a read that failed must not vanish as though nothing had been logged.**
     `ever` is empty when the request errored just as it is when the franchise has never translated
     anything, so `hasUsage(ever)` answered false either way and the panel disappeared - which reads
     as *this franchise has never switched translation on*, on the screen somebody investigating a
     translation bill would look at.
     A muted line rather than the Tower's red banner: this is a diagnostic sub-panel, and a franchise
     page turning red because a usage read hiccuped would be noise about a fact nobody asked for. What
     it must not do is answer the question it was not asked. */
  if (everQuery.isError) {
    return (
      <p className="text-xs text-muted-foreground" data-translation-usage-unreachable>
        <T>Použitie prekladov sa nepodarilo načítať.</T>
      </p>
    )
  }
  if (everQuery.isPending || !hasUsage(ever)) {
    // Nothing logged is the ordinary state of a franchise that has never switched translation on,
    // and an empty panel of zeroes says less than no panel (§9.410's distinction).
    return null
  }

  const { totals, recentErrors } = usage
  const tokens = totals.inputTokens + totals.outputTokens
  const dominant = dominantFailure(recentErrors)
  const adviceText = dominant === null ? '' : failureAdvice(dominant.status)
  const advice =
    dominant === null || adviceText === ''
      ? null
      : { status: dominant.status, text: `${dominant.count}× ${dominant.status} - ${adviceText}` }

  return (
    <section
      className="rounded-2xl border border-border p-3 space-y-2"
      data-translation-usage
      data-translation-errors={String(recentErrors.length)}
    >
      <div className="flex flex-wrap items-baseline gap-2">
        <p className="text-[11px] font-bold uppercase tracking-wider text-muted-foreground">
          <T>Preklady AI</T>
        </p>
        {/* FB-828, §3.157 - **FB-808 fixed the `ever` read on this file and left this one.** The two calls share a
            query key only while the default range is selected, so picking *Deň* issues a second request - and that
            one failing left `translationUsageOf(undefined)`'s empty totals rendering `0 v poriadku · 0,00 €` on the
            screen somebody investigating a translation bill looks at. The sentence **names the range**, because
            `ever`'s own summary is still above it and an operator has to be able to tell *this period could not be
            read* from *nothing has ever been logged*. */}
        {query.isError ? (
          <span className="text-[11px] text-amber-500" data-translation-period-unreadable>
            <T>Obdobie</T> {usageRangeLabel(range)} <T>sa nepodarilo načítať - čísla nižšie by boli nulové.</T>
          </span>
        ) : (
        <span className="text-[11px] text-muted-foreground" data-translation-totals>
          {formatCount(totals.okCalls)} <T>v poriadku</T>
          {totals.errorCalls > 0 && (
            <>
              {', '}
              <span className="text-amber-500">
                {formatCount(totals.errorCalls)} <T>zlyhalo</T>
              </span>
            </>
          )}
          {' · '}
          {formatCost(totals.costEur)}
        </span>
        )}
        {totals.lastCallAt !== null && (
          /* Legacy's *Posledné volanie*, and the answer to the support question this panel exists
           * for: a key rejected since July and a key rejected an hour ago are different problems. */
          <span className="ml-auto text-[11px] text-muted-foreground" data-translation-last>
            <T>Naposledy</T> {whenOf(totals.lastCallAt)}
          </span>
        )}
      </div>

      {/* Legacy's seven ranges - §3.98. The hard-coded `all` was the only period this panel could
        * be asked about, and *has the key been rejected all month or only since the change at
        * lunchtime* is not answerable from it. `ButtonGroup` rather than a hand-rolled row: it is
        * what the manager's Statistics, Reports and Revenue screens use for exactly this. */}
      <ButtonGroup
        value={range}
        // `ButtonGroup` speaks `string`, so the value comes back widened. Narrowed by looking it up
        // in the list the buttons were built from rather than by asserting it, which keeps the one
        // impossible case - a value not in `USAGE_RANGES` - landing on the default instead of
        // sending the server a range it will silently read as a rolling month.
        onChange={(chosen) => setRange(USAGE_RANGES.find((option) => option === chosen) ?? DEFAULT_USAGE_RANGE)}
        options={USAGE_RANGES.map((option) => ({
          value: option,
          label: usageRangeLabel(option),
        }))}
      />

      {/* The four figures legacy puts at the top of this section, of which this panel drew three in
        * prose and one not at all. `costEur` without the token counts is a bill with no basis -
        * §3.93's shape, on a smaller screen. */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2" data-translation-stats>
        <StatCard
          label={tt('Volaní')}
          value={formatCount(totals.totalCalls)}
          sub={`${formatCount(totals.okCalls)} ${tt('v poriadku')}`}
          testId="usage-calls"
        />
        <StatCard
          label={tt('Chýb')}
          value={formatCount(totals.errorCalls)}
          color={totals.errorCalls > 0 ? 'amber' : 'primary'}
          testId="usage-errors"
        />
        <StatCard
          label={tt('Tokenov')}
          value={formatCount(tokens)}
          // In and out separately, because they are billed at different rates and a run that is all
          // output is a different kind of expensive from one that is all input.
          sub={`${formatCount(totals.inputTokens)} / ${formatCount(totals.outputTokens)}`}
          testId="usage-tokens"
        />
        <StatCard
          label={tt('Náklady')}
          value={formatCost(totals.costEur)}
          color="emerald"
          testId="usage-cost"
        />
      </div>

      {usage.byLanguage.length > 0 && (
        /* Which languages this franchise is paying to translate into, and what each costs - drawn by
         * legacy's super-admin and by AGOS's own manager panel, and by neither Tower screen. It is
         * also the only place the cost decomposes: one expensive language is actionable, a single
         * total is not. */
        <div className="divide-y divide-border" data-translation-languages={usage.byLanguage.length}>
          {usage.byLanguage.map((language) => (
            <div
              key={language.lang}
              className="flex items-center justify-between gap-3 py-1 text-xs"
              data-translation-language={language.lang}
            >
              <span className="font-medium uppercase">{language.lang}</span>
              <span className="text-muted-foreground">
                {formatCount(language.calls)} <T>volaní</T>
              </span>
              <span className="text-muted-foreground tabular-nums">
                {formatCount(language.inputTokens + language.outputTokens)} <T>tok.</T>
              </span>
              <span className="tabular-nums font-semibold">{formatCost(language.costEur)}</span>
            </div>
          ))}
        </div>
      )}

      {!hasUsage(usage) && (
        /* **The state a range switcher creates and nothing else can.** The panel is only rendered
         * when the franchise has usage *somewhere*, so an empty chosen period is an answer - and
         * saying so is what stops a row of zeroes reading as a broken report. */
        <p className="text-xs text-muted-foreground" data-translation-range-empty={range}>
          {emptyRangeNote(range)}
        </p>
      )}

      {/* The diagnosis above the evidence. 2 754 of the owner's rows are one status, and a support
        * person reading twenty identical lines has to work out for themselves that the key is
        * rejected; this says it. Only drawn when one reason accounts for most of the list, because a
        * mixed list is not a diagnosis. */}
      {advice !== null && (
        <p className="text-xs font-semibold text-amber-500" data-translation-advice={advice.status}>
          {advice.text}
        </p>
      )}

      {recentErrors.length === 0 ? (
        /* **Not said when nothing ran at all.** *Nothing was translated in this period* and *no
         * translation failed in this period* are both true of an empty range, and printing them
         * together reads as two findings where there is one. The clean note is the useful one only
         * when there were calls and none of them failed. */
        hasUsage(usage) ? (
          <p className="text-xs text-muted-foreground" data-translation-clean>
            <T>Žiadne zlyhané preklady v tomto období.</T>
          </p>
        ) : null
      ) : (
        <div className="space-y-1">
          {/* The heading counts what is drawn, which is what legacy's does not. */}
          <p className="text-[11px] font-bold uppercase tracking-wider text-amber-500">
            <T>Posledné chyby</T> ({recentErrors.length})
          </p>
          {recentErrors.map((error, index) => (
            <div
              key={`${error.createdAt ?? ''}-${index}`}
              className="rounded-xl border border-amber-500/20 bg-amber-500/5 px-2 py-1.5 text-xs space-y-0.5"
              data-translation-error={error.status}
            >
              <div className="flex flex-wrap items-baseline gap-2 font-mono text-amber-500">
                <span>{error.status}</span>
                {error.targetLang !== null && <span>&rarr; {error.targetLang}</span>}
                {error.createdAt !== null && (
                  <span className="ml-auto text-muted-foreground">{whenOf(error.createdAt)}</span>
                )}
              </div>
              {error.errorMessage !== null && (
                /* Wrapped, not truncated: a rejected key answers with a sentence naming the domain
                 * or the account, and that sentence is the whole value of the row. */
                <p className="break-words text-muted-foreground" data-translation-error-message>
                  {error.errorMessage}
                </p>
              )}
              {error.sourceExcerpt !== null && (
                <p className="break-words text-[11px] text-muted-foreground/80" data-translation-source>
                  <T>Text</T>: {error.sourceExcerpt}
                </p>
              )}
            </div>
          ))}
        </div>
      )}
    </section>
  )
}

/**
 * `created_at` as a person reads it.
 *
 * **Postgres text, not ISO** - `2026-07-28 18:30:56.500302+00`, which `new Date()` parses in every
 * browser this app supports but which `Date.parse` is not specified to accept. An unparseable value
 * is shown as it came rather than as *Invalid Date*.
 */
export function whenOf(createdAt: string): string {
  const parsed = new Date(createdAt.replace(' ', 'T'))
  if (Number.isNaN(parsed.getTime())) return createdAt
  return parsed.toLocaleString('sk-SK')
}

/** The sentence a support person needs when every call is refused for one reason. */
export function dominantFailure(
  errors: readonly { status: string }[],
): { status: string; count: number } | null {
  if (errors.length === 0) return null
  const counts = new Map<string, number>()
  for (const error of errors) {
    counts.set(error.status, (counts.get(error.status) ?? 0) + 1)
  }
  const ranked = [...counts.entries()].sort((left, right) => right[1] - left[1])
  const top = ranked[0]
  if (top === undefined) return null
  const [status, count] = top
  // Only worth saying when one reason accounts for most of them; a mixed list is not a diagnosis.
  return count * 2 > errors.length ? { status, count } : null
}

/** What that reason means, for the statuses support can actually act on. */
export function failureAdvice(status: string): string {
  if (status === 'HTTP_401' || status === 'INVALID_API_KEY') {
    return tt('Kľúč Anthropic je odmietnutý - skontrolujte ho v integráciách franšízy.')
  }
  if (status === 'HTTP_429' || status === 'RATE_LIMIT') {
    return tt('Limit Anthropic je vyčerpaný - preklady sa obnovia samy, alebo treba vyšší limit.')
  }
  if (status === 'HTTP_403' || status === 'FORBIDDEN') {
    return tt('Účet Anthropic nemá na tento model právo.')
  }
  if (status === 'INSUFFICIENT_CREDITS') {
    return tt('Na účte Anthropic nie je kredit.')
  }
  if (status === 'TRANSPORT' || status === 'INTERRUPTED') {
    return tt('Anthropic nebol dosiahnuteľný - ide o dočasnú chybu siete.')
  }
  return ''
}
